import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Loader2, Link2, Copy, Check, Trash2, Activity, ExternalLink, Plus, X, Bell, BellOff, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const fmtUSD = (n) => "$" + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * Manage lender-shareable read-only links for any PFS snapshot.
 *
 * Two views:
 *   1. Picker — pass a `snapshotId` prop to scope the dialog to a single
 *      snapshot's links + show a "Generate new link" form.
 *   2. List   — pass `snapshotId={null}` to show every link the user has
 *      ever generated, across all snapshots.
 *
 * Backend endpoints used:
 *   POST   /api/pfs/history/{snapId}/share
 *   GET    /api/pfs/shares
 *   GET    /api/pfs/shares/{linkId}/audit
 *   DELETE /api/pfs/shares/{linkId}
 */
export default function PfsShareDialog({ open, onOpenChange, snapshotId, snapshotLabel, focusLinkId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [recipient, setRecipient] = useState("");
  const [label, setLabel] = useState("");
  const [password, setPassword] = useState("");
  const [expiresDays, setExpiresDays] = useState(14);
  const [maxViews, setMaxViews] = useState("");
  const [notifyOnView, setNotifyOnView] = useState(true);
  const [emailOnView, setEmailOnView] = useState(false);
  const [creating, setCreating] = useState(false);

  // Per-row state
  const [copiedId, setCopiedId] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [auditViews, setAuditViews] = useState([]);
  const [auditBusy, setAuditBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pfs/shares");
      let list = r.data?.items || [];
      if (snapshotId) list = list.filter((s) => s.snapshot_id === snapshotId);
      setItems(list);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load share links");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      refresh().then(() => {
        // If the dialog was opened to spotlight a specific link (via
        // notification click), auto-expand its audit log.
        if (focusLinkId) {
          showAudit({ id: focusLinkId });
        }
      });
      setShowForm(!!snapshotId && items.length === 0);
      if (!focusLinkId) {
        setAuditFor(null);
        setAuditViews([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, snapshotId, focusLinkId]);

  const create = async (e) => {
    e?.preventDefault();
    if (!snapshotId) return;
    setCreating(true);
    try {
      const body = {
        recipient_label: recipient || null,
        label: label || null,
        password: password || null,
        expires_in_days: expiresDays ? Number(expiresDays) : null,
        max_views: maxViews ? Number(maxViews) : null,
        notify_on_view: !!notifyOnView,
        email_on_view: !!emailOnView,
      };
      const r = await api.post(`/pfs/history/${snapshotId}/share`, body);
      setItems((prev) => [{ ...r.data, status: "active" }, ...prev]);
      // Auto-copy the new URL so the user can paste it immediately.
      try {
        await navigator.clipboard.writeText(r.data.url);
        toast.success("Share link created — URL copied to clipboard");
      } catch {
        toast.success("Share link created");
      }
      setRecipient("");
      setLabel("");
      setPassword("");
      setMaxViews("");
      setExpiresDays(14);
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create link");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (item) => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId((c) => (c === item.id ? null : c)), 1800);
    } catch {
      toast.error("Clipboard unavailable — copy the URL manually");
    }
  };

  const revoke = async (item) => {
    if (!window.confirm(`Revoke link for "${item.recipient_label || item.label || "this lender"}"? They will no longer be able to view the PFS.`)) return;
    try {
      await api.delete(`/pfs/shares/${item.id}`);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "revoked", revoked_at: new Date().toISOString() } : i)));
      toast.success("Link revoked");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not revoke");
    }
  };

  const showAudit = async (item) => {
    if (auditFor === item.id) {
      setAuditFor(null);
      setAuditViews([]);
      return;
    }
    setAuditFor(item.id);
    setAuditBusy(true);
    try {
      const r = await api.get(`/pfs/shares/${item.id}/audit`);
      setAuditViews(r.data?.views || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load audit");
      setAuditViews([]);
    } finally {
      setAuditBusy(false);
    }
  };

  const togglePref = async (item, key) => {
    try {
      const r = await api.patch(`/pfs/shares/${item.id}`, { [key]: !item[key] });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...r.data } : i)));
      toast.success(`${key === "notify_on_view" ? "Alerts" : "Email alerts"} ${!item[key] ? "on" : "off"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
    }
  };

  const statusChip = (s) => {
    const status = s.status || "active";
    const palette = {
      active: "bg-emerald-50 border-emerald-200 text-emerald-900",
      revoked: "bg-rose-50 border-rose-200 text-rose-900",
      expired: "bg-slate-100 border-slate-200 text-slate-700",
      capped: "bg-amber-50 border-amber-200 text-amber-900",
    }[status] || "bg-slate-100 border-slate-200 text-slate-700";
    return <span className={`text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-sm border ${palette}`}>{status}</span>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="pfs-share-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-emerald-600" />
            Lender share links {snapshotLabel ? `— ${snapshotLabel}` : ""}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Generate a no-account URL the lender can open to view this exact snapshot in read-only mode.
            Optional password, expiry, and max-views — every view is logged.
          </DialogDescription>
        </DialogHeader>

        {snapshotId && (
          <div className="mb-3">
            {!showForm ? (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="btn-primary-dark text-xs inline-flex items-center gap-1 px-3"
                data-testid="share-new-link-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Generate new link
              </button>
            ) : (
              <form onSubmit={create} className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-2" data-testid="share-create-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <FormField label="Recipient (visible to lender)" testid="share-recipient">
                    <input className="input-clean w-full" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Sarah at Wells Fargo" maxLength={120} />
                  </FormField>
                  <FormField label="Internal label" testid="share-label">
                    <input className="input-clean w-full" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Wells Fargo refi" maxLength={120} />
                  </FormField>
                  <FormField label="Password (optional)" testid="share-password">
                    <input type="text" className="input-clean w-full" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank for no password" />
                  </FormField>
                  <div className="grid grid-cols-2 gap-2">
                    <FormField label="Expires in (days)" testid="share-expires">
                      <input type="number" min={1} max={365} className="input-clean w-full" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} />
                    </FormField>
                    <FormField label="Max views" testid="share-max-views">
                      <input type="number" min={1} max={1000} className="input-clean w-full" value={maxViews} onChange={(e) => setMaxViews(e.target.value)} placeholder="∞" />
                    </FormField>
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-1 flex-wrap text-sm">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-slate-700" data-testid="share-notify-on-view">
                    <input type="checkbox" className="rounded-sm" checked={notifyOnView} onChange={(e) => setNotifyOnView(e.target.checked)} />
                    Alert me on every view
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-slate-700" data-testid="share-email-on-view">
                    <input type="checkbox" className="rounded-sm" checked={emailOnView} onChange={(e) => setEmailOnView(e.target.checked)} />
                    Also email me
                  </label>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button type="submit" disabled={creating} className="btn-primary-dark text-xs inline-flex items-center gap-1 px-3 disabled:opacity-60" data-testid="share-create-submit">
                    {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                    Generate &amp; copy
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="text-xs text-slate-500 hover:text-slate-800 px-2">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">
            No share links {snapshotId ? "for this snapshot" : ""} yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200" data-testid="pfs-share-list">
            {items.map((s) => (
              <li key={s.id} className="py-3" data-testid={`share-row-${s.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-start">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">
                        {s.recipient_label || s.label || "Untitled link"}
                      </span>
                      {statusChip(s)}
                      {s.has_password && <span className="text-[10px] uppercase tracking-widest font-mono text-slate-500">password</span>}
                      <button
                        type="button"
                        onClick={() => togglePref(s, "notify_on_view")}
                        className={`text-[10px] uppercase tracking-widest font-mono inline-flex items-center gap-1 px-1 ${s.notify_on_view ? "text-emerald-700" : "text-slate-400"}`}
                        title={s.notify_on_view ? "Alerts on — click to mute" : "Click to enable alerts"}
                        data-testid={`share-toggle-notify-${s.id}`}
                      >
                        {s.notify_on_view ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                        {s.notify_on_view ? "alerts" : "muted"}
                      </button>
                      {s.email_on_view && (
                        <button
                          type="button"
                          onClick={() => togglePref(s, "email_on_view")}
                          className="text-[10px] uppercase tracking-widest font-mono inline-flex items-center gap-1 px-1 text-emerald-700"
                          title="Email alerts on — click to disable"
                          data-testid={`share-toggle-email-${s.id}`}
                        >
                          <Mail className="w-3 h-3" /> email
                        </button>
                      )}
                    </div>
                    <div className="font-mono text-[11px] text-slate-500 truncate mt-0.5">
                      {s.url}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3">
                      <span>Views: <b>{s.views}{s.max_views ? `/${s.max_views}` : ""}</b></span>
                      {s.expires_at && <span>Expires {new Date(s.expires_at).toLocaleDateString()}</span>}
                      <span>Created {new Date(s.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => copy(s)} className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1" data-testid={`share-copy-${s.id}`} title="Copy URL">
                      {copiedId === s.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      {copiedId === s.id ? "Copied" : "Copy"}
                    </button>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1" data-testid={`share-open-${s.id}`}>
                      <ExternalLink className="w-3.5 h-3.5" /> Open
                    </a>
                    <button onClick={() => showAudit(s)} className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1" data-testid={`share-audit-${s.id}`}>
                      <Activity className="w-3.5 h-3.5" /> Audit
                    </button>
                    {s.status !== "revoked" && (
                      <button onClick={() => revoke(s)} className="text-xs text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 px-2 py-1" data-testid={`share-revoke-${s.id}`}>
                        <X className="w-3.5 h-3.5" /> Revoke
                      </button>
                    )}
                    <button onClick={() => revoke(s)} disabled={s.status !== "revoked"} hidden>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {auditFor === s.id && (
                  <div className="mt-2 ml-2 bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid={`share-audit-block-${s.id}`}>
                    <div className="overline text-slate-500 mb-1">Audit log {auditBusy && <Loader2 className="inline w-3 h-3 animate-spin ml-2" />}</div>
                    {!auditBusy && auditViews.length === 0 ? (
                      <div className="text-xs text-slate-500">No views yet.</div>
                    ) : (
                      <ul className="text-xs text-slate-700 space-y-0.5">
                        {auditViews.map((v) => (
                          <li key={v.id} className="font-mono">
                            <span className="text-slate-500">{new Date(v.viewed_at).toLocaleString()}</span>
                            {" — "}<b>{v.action}</b>
                            {v.ip && <span className="text-slate-400"> from {v.ip}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, testid, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-500 mb-1">{label}</div>
      <div data-testid={testid}>{children}</div>
    </label>
  );
}

// fmtUSD is exported for potential reuse but not needed here — tree-shaken.
export { fmtUSD };
